import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';

import type { DeliveryRunDetail } from '../../types/delivery-runs.types';

/** Reads a string/number/array field off the loosely-typed `lineage` block. */
function lineageField<T>(
  run: DeliveryRunDetail,
  key: string,
  guard: (v: unknown) => v is T,
): T | null {
  const value = run.lineage[key];
  return guard(value) ? value : null;
}
const isString = (v: unknown): v is string => typeof v === 'string' && v !== '';
const isNumber = (v: unknown): v is number => typeof v === 'number';
const isStringArray = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((x) => typeof x === 'string');

/** Retry chain for the Relationships tab: parent run and retry runs, each switchable in place. */
export function RetryChain({
  run,
  onSelect,
  testId = 'retry-chain',
}: {
  run: DeliveryRunDetail;
  onSelect: (runId: string) => void;
  /** `data-testid` root; each linked run gets `${testId}-item-{runId}` / `-view-button-{runId}`. */
  testId?: string;
}) {
  const { t } = useTranslation('deliveries');
  const parent = lineageField(run, 'parentRunId', isString);
  const children = lineageField(run, 'childRunIds', isStringArray) ?? [];
  const attempt = lineageField(run, 'retryAttempt', isNumber) ?? 0;
  const max = lineageField(run, 'maxRetryAttempts', isNumber);

  return (
    <section data-testid={testId} className="bg-background space-y-2 rounded-lg border p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="text-foreground text-sm font-semibold">{t('inspector.lineage.title')}</h4>
        {attempt > 0 && max !== null && (
          <span className="text-muted-foreground text-xs">
            {t('inspector.lineage.attemptOf', { n: attempt, max })}
          </span>
        )}
      </div>
      {!parent && children.length === 0 ? (
        <p data-testid={`${testId}-empty`} className="text-muted-foreground text-sm">
          {t('inspector.lineage.none')}
        </p>
      ) : (
        <ul className="space-y-1.5 text-sm">
          {parent && (
            <ChainRow
              label={t('inspector.lineage.retriedFrom')}
              runId={parent}
              onSelect={onSelect}
              testId={testId}
            />
          )}
          <li className="text-foreground flex items-center gap-2">
            <span className="text-muted-foreground w-36 shrink-0 text-xs">
              {t('inspector.lineage.thisRun')}
            </span>
            <span className="font-mono text-xs break-all">{run.id}</span>
          </li>
          {/* Every direct retry of this run is attempt `attempt + 1` server-side —
              siblings share the number — so several are told apart by order (#k). */}
          {children.map((id, i) => (
            <ChainRow
              key={id}
              label={
                children.length > 1
                  ? t('inspector.lineage.retryAttemptSibling', { n: attempt + 1, k: i + 1 })
                  : t('inspector.lineage.retryAttempt', { n: attempt + 1 })
              }
              runId={id}
              onSelect={onSelect}
              testId={testId}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function ChainRow({
  label,
  runId,
  onSelect,
  testId,
}: {
  label: string;
  runId: string;
  onSelect: (runId: string) => void;
  testId: string;
}) {
  const { t } = useTranslation('deliveries');
  return (
    <li data-testid={`${testId}-item-${runId}`} className="flex items-center gap-2">
      <span className="text-muted-foreground w-36 shrink-0 text-xs">{label}</span>
      <span className="text-foreground font-mono text-xs break-all">{runId}</span>
      <Button
        type="button"
        variant="link"
        className="h-auto shrink-0 cursor-pointer p-0 text-xs"
        data-testid={`${testId}-view-button-${runId}`}
        onClick={() => {
          onSelect(runId);
        }}
      >
        {t('inspector.lineage.view')}
      </Button>
    </li>
  );
}
